import React, { useState, useRef, useEffect } from 'react';
import { Camera, Mic, Square, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

// Detect whether we're running inside the BrainSync Android wrapper -
// if so we use the native speech recognizer instead of MediaRecorder
// (the WebView shipped with TWAs doesn't always expose MediaRecorder
// reliably, and the native path produces a transcript directly).
const hasNativeVoiceBridge = () =>
    typeof window !== 'undefined' &&
    typeof window.BrainSyncVoice?.startListening === 'function';

// Native camera bridge - bypasses WebView's file chooser entirely (which on
// some devices ignores `capture="environment"` and shows the gallery).
const hasNativeCameraBridge = () =>
    typeof window !== 'undefined' &&
    typeof window.BrainSyncCamera?.takePicture === 'function';

/**
 * QuickCaptureButtons - instant-capture pair (camera + mic) sitting next to
 * the regular Add (+) button on the dashboard header. Both bypass the
 * categories/tags modal: the bookmark is created instantly with status =
 * "processing" and the existing background pipeline auto-summarises +
 * auto-categorises.
 *
 * Props:
 *  - onBookmarkAdded(bookmarkData): called optimistically so dashboard can
 *    prepend the new bookmark with a "Processing…" indicator.
 *  - onLimitReached(detail): called when the API returns 403 bookmark_limit_reached.
 *  - disabled: hides interaction while another quick capture is in-flight.
 */
export default function QuickCaptureButtons({ onBookmarkAdded, onLimitReached, disabled }) {
    const cameraInputRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const recordingStreamRef = useRef(null);
    const recordingTimerRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [recording, setRecording] = useState(false);
    const [recordingSecs, setRecordingSecs] = useState(0);

    /* ─────────────── Camera capture ─────────────── */

    /* ─────────────── Camera capture ─────────────── */

    // Listen for native camera results in TWA. After the user takes a photo,
    // BrainSyncCamera fires a `brainsync-camera-result` CustomEvent. We then
    // pull the JPEG from the bridge in chunks and POST it to /bookmarks/manual.
    useEffect(() => {
        if (!hasNativeCameraBridge()) return;
        const handler = async (e) => {
            const { ok, error } = e.detail || {};
            if (!ok) {
                if (error && error !== 'Cancelled' && error !== 'Permission denied') {
                    toast.error(`Camera: ${error}`);
                }
                setBusy(false);
                return;
            }
            try {
                const bridge = window.BrainSyncCamera;
                const total = bridge.getCapturedImageLength?.() ?? 0;
                if (!total) {
                    toast.error('Captured photo was empty');
                    setBusy(false);
                    return;
                }
                const chunkSize = 256 * 1024;
                let b64 = '';
                for (let off = 0; off < total; off += chunkSize) {
                    b64 += bridge.getCapturedImageChunk(off, chunkSize);
                }
                const mime = bridge.getCapturedImageMime?.() || 'image/jpeg';
                const filename = bridge.getCapturedImageFilename?.() || `photo-${Date.now()}.jpg`;
                bridge.clearCapturedImage?.();

                const byteChars = atob(b64);
                const byteNums = new Uint8Array(byteChars.length);
                for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
                const file = new File([byteNums], filename, { type: mime });
                await uploadImage(file);
            } catch (err) {
                toast.error(`Could not read photo: ${err?.message || err}`);
                setBusy(false);
            }
        };
        window.addEventListener('brainsync-camera-result', handler);
        return () => window.removeEventListener('brainsync-camera-result', handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Tiny tactile tick on tap - feels much more responsive on Android,
    // especially when the native bridges introduce a few-ms launch delay.
    const haptic = () => {
        try { navigator.vibrate?.(15); } catch (_) { /* iOS / Safari - silent */ }
    };

    const handleCameraClick = () => {
        if (busy || recording) return;
        haptic();
        // Native bridge path - TWA on Android.
        if (hasNativeCameraBridge()) {
            try {
                setBusy(true);
                window.BrainSyncCamera.takePicture();
            } catch (err) {
                setBusy(false);
                toast.error('Could not open camera');
            }
            return;
        }
        cameraInputRef.current?.click();
    };

    const handleFileSelected = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // allow re-picking the same file
        if (!file) return;
        await uploadImage(file);
    };

    const uploadImage = async (file) => {
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            // No category_ids / tags / notes - backend pipeline auto-fills them.

            const res = await fetch(`${API}/bookmarks/manual`, {
                method: 'POST',
                credentials: 'include',
                body: fd,
            });

            if (res.status === 403) {
                const err = await res.json().catch(() => ({}));
                if (err?.detail?.error === 'bookmark_limit_reached') {
                    onLimitReached?.(err.detail);
                    return;
                }
            }
            if (!res.ok) {
                const txt = await res.text().catch(() => '');
                throw new Error(`Upload failed (${res.status}) ${txt.slice(0, 120)}`);
            }

            const data = await res.json();
            onBookmarkAdded?.(data);
            toast.success('Photo saved - processing in background');
        } catch (err) {
            toast.error(err?.message || 'Could not save photo');
        } finally {
            setBusy(false);
        }
    };

    /* ─────────────── Voice capture ─────────────── */

    // Listen for native voice results in TWA - when the user finishes speaking,
    // BrainSyncVoice fires a `brainsync-voice-result` CustomEvent with the
    // transcript. We then submit it as a text bookmark to the existing
    // /bookmarks/manual endpoint (which auto-summarises + auto-tags).
    useEffect(() => {
        if (!hasNativeVoiceBridge()) return;
        const handler = async (e) => {
            const { text, cancelled } = e.detail || {};
            stopTimer();
            setRecording(false);
            if (!text || cancelled) {
                if (!cancelled) toast.error('Could not recognise voice');
                return;
            }
            await uploadVoiceText(text.trim());
        };
        window.addEventListener('brainsync-voice-result', handler);
        return () => window.removeEventListener('brainsync-voice-result', handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startRecording = async () => {
        if (busy || recording) return;
        haptic();

        // Native bridge path - TWA on Android. Speech is recognised by
        // Google's on-device recognizer; we get the final transcript via the
        // brainsync-voice-result event handled above.
        if (hasNativeVoiceBridge()) {
            try {
                window.BrainSyncVoice.startListening();
                setRecording(true);
                setRecordingSecs(0);
                recordingTimerRef.current = setInterval(() => {
                    setRecordingSecs(prev => prev + 1);
                }, 1000);
            } catch (err) {
                toast.error('Could not start voice input');
            }
            return;
        }

        if (typeof window === 'undefined' || !navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
            toast.error('Voice recording is not supported in this browser');
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            recordingStreamRef.current = stream;

            // Pick a mime type the recorder supports (webm/opus is universal in Chrome,
            // mp4/aac is what iOS Safari accepts)
            const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
            const mimeType = candidates.find(t => window.MediaRecorder.isTypeSupported?.(t)) || '';
            const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
            audioChunksRef.current = [];

            recorder.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
            };
            recorder.onstop = async () => {
                const chunks = audioChunksRef.current;
                audioChunksRef.current = [];
                stopMicStream();
                stopTimer();
                setRecording(false);

                const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
                if (!blob.size) {
                    toast.error('Recording was empty');
                    return;
                }
                await uploadVoice(blob);
            };

            recorder.start();
            mediaRecorderRef.current = recorder;
            setRecording(true);
            setRecordingSecs(0);
            recordingTimerRef.current = setInterval(() => {
                setRecordingSecs(prev => {
                    const next = prev + 1;
                    // Auto-stop at 5 minutes to keep transcripts under Whisper's 25MB cap
                    if (next >= 300) {
                        try { recorder.state === 'recording' && recorder.stop(); } catch (_) {}
                    }
                    return next;
                });
            }, 1000);
        } catch (err) {
            stopMicStream();
            const code = err?.name || '';
            if (code === 'NotAllowedError' || code === 'SecurityError') {
                toast.error('Microphone permission denied');
            } else if (code === 'NotFoundError') {
                toast.error('No microphone available');
            } else {
                toast.error(`Could not start recording${err?.message ? `: ${err.message}` : ''}`);
            }
        }
    };

    const stopRecording = () => {
        haptic();
        // Native bridge - Android speech recognizer auto-stops when the user
        // finishes speaking; tapping again is just a UI cancel signal.
        if (hasNativeVoiceBridge()) {
            stopTimer();
            setRecording(false);
            return;
        }
        const recorder = mediaRecorderRef.current;
        if (!recorder || recorder.state === 'inactive') return;
        try { recorder.stop(); } catch (_) { /* ignore */ }
    };

    const stopMicStream = () => {
        const stream = recordingStreamRef.current;
        recordingStreamRef.current = null;
        try { stream?.getTracks().forEach(t => t.stop()); } catch (_) {}
    };

    const stopTimer = () => {
        if (recordingTimerRef.current) {
            clearInterval(recordingTimerRef.current);
            recordingTimerRef.current = null;
        }
    };

    const uploadVoice = async (blob) => {
        setBusy(true);
        try {
            const ext = blob.type.includes('mp4') ? 'm4a' : (blob.type.includes('ogg') ? 'ogg' : 'webm');
            const fd = new FormData();
            fd.append('file', blob, `voice-${Date.now()}.${ext}`);

            const res = await fetch(`${API}/bookmarks/voice-audio`, {
                method: 'POST',
                credentials: 'include',
                body: fd,
            });

            if (res.status === 403) {
                const err = await res.json().catch(() => ({}));
                if (err?.detail?.error === 'bookmark_limit_reached') {
                    onLimitReached?.(err.detail);
                    return;
                }
            }
            if (!res.ok) {
                let detail = '';
                try { detail = (await res.json())?.detail || ''; } catch (_) {
                    detail = await res.text().catch(() => '');
                }
                throw new Error(detail || `Upload failed (${res.status})`);
            }
            const data = await res.json();
            onBookmarkAdded?.(data);
            toast.success('Voice note saved - transcribing in background');
        } catch (err) {
            toast.error(err?.message || 'Could not save voice note');
        } finally {
            setBusy(false);
        }
    };

    /**
     * Used by the TWA native voice path: the Android SpeechRecognizer returned
     * a transcript directly, so we skip Whisper and hit /bookmarks/manual with
     * `text_content` (existing endpoint that already handles auto-summary +
     * auto-tagging + auto-categorization).
     */
    const uploadVoiceText = async (transcript) => {
        if (!transcript) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append('text_content', transcript);

            const res = await fetch(`${API}/bookmarks/manual`, {
                method: 'POST',
                credentials: 'include',
                body: fd,
            });

            if (res.status === 403) {
                const err = await res.json().catch(() => ({}));
                if (err?.detail?.error === 'bookmark_limit_reached') {
                    onLimitReached?.(err.detail);
                    return;
                }
            }
            if (!res.ok) {
                let detail = '';
                try { detail = (await res.json())?.detail || ''; } catch (_) {
                    detail = await res.text().catch(() => '');
                }
                throw new Error(detail || `Upload failed (${res.status})`);
            }
            const data = await res.json();
            onBookmarkAdded?.(data);
            toast.success('Voice note saved - processing in background');
        } catch (err) {
            toast.error(err?.message || 'Could not save voice note');
        } finally {
            setBusy(false);
        }
    };

    const formatTime = (s) => {
        const m = Math.floor(s / 60);
        const sec = s % 60;
        return `${m}:${sec.toString().padStart(2, '0')}`;
    };

    /* ─────────────── Render ─────────────── */

    return (
        <div className="flex items-center gap-1.5" data-testid="quick-capture-buttons">
            <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelected}
                className="hidden"
                data-testid="quick-capture-camera-input"
            />

            <button
                type="button"
                onClick={handleCameraClick}
                disabled={disabled || busy || recording}
                title="Snap a photo"
                aria-label="Snap a photo"
                className="rounded-xl h-10 w-10 flex items-center justify-center bg-secondary hover:bg-secondary/80 text-foreground transition-colors disabled:opacity-50"
                data-testid="quick-capture-camera-btn"
            >
                {busy && !recording ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                    <Camera className="w-4 h-4" />
                )}
            </button>

            <button
                type="button"
                onClick={recording ? stopRecording : startRecording}
                disabled={disabled || busy}
                title={recording ? 'Stop recording' : 'Record a voice note'}
                aria-label={recording ? 'Stop recording' : 'Record a voice note'}
                className={`rounded-xl h-10 flex items-center justify-center transition-colors disabled:opacity-50 ${
                    recording
                        ? 'bg-red-500 hover:bg-red-600 text-white px-3 gap-1.5'
                        : 'bg-secondary hover:bg-secondary/80 text-foreground w-10'
                }`}
                data-testid="quick-capture-mic-btn"
            >
                {recording ? (
                    <>
                        <Square className="w-3.5 h-3.5 fill-current" />
                        <span className="text-xs font-mono tabular-nums">{formatTime(recordingSecs)}</span>
                    </>
                ) : busy ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                    <Mic className="w-4 h-4" />
                )}
            </button>
        </div>
    );
}
